import { memo, useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  Users,
  GraduationCap,
  ArrowRight,
  CheckCircle,
  Sparkles,
} from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { RouteKey } from '@/i18n/routes'

import gestioneImg from '@/assets/images/sections/GestioneHomeTeaser.jpg'
import consulenzaImg from '@/assets/images/sections/ConsulenzaTeaserHome.jpg'
import formazioneImg from '@/assets/images/sections/FormazioneBF.jpeg'

interface ServiceData {
  id: string
  titleKey: string
  descriptionKey: string
  featuresKey: string
  icon: typeof Building2
  routeKey: RouteKey
  image: string
  color: string
  bgColor: string
  accentColor: string
}

const services: ServiceData[] = [
  {
    id: 'management',
    titleKey: 'services.management.title',
    descriptionKey: 'services.management.description',
    featuresKey: 'services.management.features',
    icon: Building2,
    routeKey: 'management',
    image: gestioneImg,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50',
    accentColor: 'text-amber-600',
  },
  {
    id: 'consulting',
    titleKey: 'services.consulting.title',
    descriptionKey: 'services.consulting.description',
    featuresKey: 'services.consulting.features',
    icon: Users,
    routeKey: 'consulting',
    image: consulenzaImg,
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-blue-50',
    accentColor: 'text-blue-600',
  },
  {
    id: 'training',
    titleKey: 'services.training.title',
    descriptionKey: 'services.training.description',
    featuresKey: 'services.training.features',
    icon: GraduationCap,
    routeKey: 'training',
    image: formazioneImg,
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50',
    accentColor: 'text-emerald-600',
  },
]

// Service Tab Button
const ServiceTab = memo(function ServiceTab({
  service,
  isActive,
  onClick,
  t,
}: {
  service: ServiceData
  isActive: boolean
  onClick: () => void
  t: (key: string) => string
}) {
  const Icon = service.icon

  return (
    <button
      onClick={onClick}
      className={`relative flex items-center gap-2 sm:gap-3 px-4 py-3 sm:px-6 sm:py-4 rounded-lg sm:rounded-xl transition-all duration-300 ${
        isActive
          ? `bg-gradient-to-r ${service.color} text-white shadow-lg scale-[1.02]`
          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
      }`}
    >
      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      <span className="text-sm sm:text-base font-semibold">{t(service.titleKey)}</span>
      {isActive && (
        <motion.div
          layoutId="activeIndicator"
          className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-0.5 sm:w-8 sm:h-1 rounded-full bg-white/50"
        />
      )}
    </button>
  )
})

// Service Content Panel
const ServicePanel = memo(function ServicePanel({
  service,
  t,
}: {
  service: ServiceData
  t: (key: string, options?: object) => string | string[]
}) {
  const Icon = service.icon
  const features = t(service.featuresKey, { returnObjects: true }) as string[]

  return (
    <motion.div
      key={service.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="grid lg:grid-cols-2 gap-8 items-center"
    >
      {/* Image */}
      <div className="relative order-2 lg:order-1">
        <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
          <img
            src={service.image}
            alt={t(service.titleKey) as string}
            className="w-full h-64 sm:h-80 lg:h-96 object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent" />

          {/* Floating badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/90 backdrop-blur-sm shadow-lg"
          >
            <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br ${service.color} flex items-center justify-center`}>
              <Icon className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            </div>
            <span className="text-sm sm:text-base font-semibold text-gray-900">{t(service.titleKey)}</span>
          </motion.div>
        </div>

        {/* Decorative elements */}
        <div className={`absolute -top-4 -right-4 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br ${service.color} opacity-20 blur-2xl`} />
        <div className={`absolute -bottom-4 -left-4 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br ${service.color} opacity-10 blur-2xl`} />
      </div>

      {/* Content */}
      <div className={`order-1 lg:order-2 ${service.bgColor} rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8`}>
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg`}>
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {t(service.titleKey)}
            </h3>
            <p className={`text-xs sm:text-sm font-medium ${service.accentColor}`}>
              BF Wellness
            </p>
          </div>
        </div>

        <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
          {t(service.descriptionKey)}
        </p>

        {/* Features */}
        <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
          {Array.isArray(features) && features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-2 sm:gap-3"
            >
              <CheckCircle className={`w-4 h-4 sm:w-5 sm:h-5 ${service.accentColor} flex-shrink-0 mt-0.5`} />
              <span className="text-sm sm:text-base text-gray-700">{feature}</span>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <LocalizedLink
          to={service.routeKey}
          className={`inline-flex items-center gap-2 px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl bg-gradient-to-r ${service.color} text-white text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 group`}
        >
          <span>{t('common:buttons.learnMore')}</span>
          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-1" />
        </LocalizedLink>
      </div>
    </motion.div>
  )
})

export function ServicesSection() {
  const { t } = useTranslation('home')
  const [activeService, setActiveService] = useState(0)

  return (
    <section className="section-padding bg-gradient-to-b from-gray-50 to-gray-100 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/2 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 opacity-40 blur-3xl translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 opacity-40 blur-3xl -translate-x-1/2 translate-y-1/2" />

      <div className="container-custom relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-gray-700 border border-gray-300 shadow-sm mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">{t('services.label')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('services.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Service Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12"
        >
          {services.map((service, index) => (
            <ServiceTab
              key={service.id}
              service={service}
              isActive={activeService === index}
              onClick={() => setActiveService(index)}
              t={t as (key: string) => string}
            />
          ))}
        </motion.div>

        {/* Active Service Content */}
        <ServicePanel
          service={services[activeService]}
          t={t as (key: string, options?: object) => string | string[]}
        />
      </div>
    </section>
  )
}
